import { useEffect, useState } from 'react';
import { Modal, Spinner, Alert } from '@components/ui';
import Mustache from 'mustache';
import { templateService } from '@features/templates/services/templateService';
import { useTemplate } from '@features/templates/hooks/useTemplate';
import type { CafeTemplate } from '@features/templates/types/template.types';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';

interface InvoicePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  wizardData: InvoiceWizardData;
}

// Payment method labels
const paymentMethodLabels: Record<string, string> = {
  '01': 'Crédito',
  '02': 'Efectivo',
  '03': 'Tarjeta de Crédito',
  '04': 'Tarjeta de Débito',
  '05': 'Tarjeta de Fidelización',
  '06': 'Vale',
  '07': 'Tarjeta de Regalo',
  '08': 'Transf./Dep. a cta. Bancaria',
  '09': 'Cheque',
  '10': 'Punto de Pago',
  '99': 'Otro'
};

// Receiver type labels
const receiverTypeLabels: Record<string, string> = {
  '01': 'Contribuyente',
  '02': 'Consumidor Final',
  '03': 'Gobierno',
  '04': 'Extranjero',
};

// Document type labels
const documentTypeLabels: Record<string, string> = {
  '01': 'Factura de Operación Interna',
  '02': 'Factura de Importación',
  '03': 'Factura de Exportación',
  '04': 'Nota de Crédito',
  '05': 'Nota de Débito',
  '06': 'Nota de Crédito Genérica',
  '07': 'Nota de Débito Genérica',
  '08': 'Factura de Zona Franca',
  '09': 'Reembolso',
  '10': 'Factura de operación extranjera',
};

// ITBMS rate mapping
const mapITBMSRate = (taxRate: number): string => {
  const rateMap: Record<number, string> = {
    0: '00',
    5: '04',
    7: '01',
    10: '02',
    15: '03',
  };
  return rateMap[taxRate] || '01';
};

// Format amount to 2 decimal places
const formatAmount = (amount: number | string | undefined | null): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (isNaN(num) || amount === null || amount === undefined) {
    return '0.00';
  }
  return num.toFixed(2);
};

// Format price with up to 6 decimal places and thousand separators (DGI format 1-9p2-6)
const formatPriceAmount = (amount: number | string | undefined | null): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (isNaN(num) || amount === null || amount === undefined) {
    return '0.00';
  }
  const full = num.toFixed(6);
  const [intPart, decPart] = full.split('.');
  let trimmed = decPart.replace(/0+$/, '');
  if (trimmed.length < 2) trimmed = trimmed.padEnd(2, '0');
  const formattedInt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(parseInt(intPart));
  return `${formattedInt}.${trimmed}`;
};

// Format date for display
const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Get current time in HH:MM:SS format
const getCurrentTime = (): string => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
};

export function InvoicePreviewModal({
  isOpen,
  onClose,
  wizardData,
}: InvoicePreviewModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderedHtml, setRenderedHtml] = useState<string>('');
  const [cafeTemplate, setCafeTemplate] = useState<CafeTemplate | null>(null);

  // Get company template settings (colors, logo)
  const { data: templateData } = useTemplate();

  // Load CAFE templates on mount
  useEffect(() => {
    const loadCafeTemplates = async () => {
      try {
        const templates = await templateService.getCafeTemplates();
        if (Array.isArray(templates) && templates.length > 0) {
          // Use default template or first available
          const defaultTemplate = templates.find((t) => t.name === 'CafeTemplateDefault') || templates[0];
          setCafeTemplate(defaultTemplate);
        }
      } catch (err) {
        console.error('Error loading CAFE templates:', err);
      }
    };

    loadCafeTemplates();
  }, []);

  // Transform wizardData to FElectronica format
  const transformWizardDataToFElectronica = (data: InvoiceWizardData) => {
    const currentDate = formatDateForDisplay(data.invoiceDate);
    const currentTime = getCurrentTime();

    // Map items to gItem format
    const gItem = data.items.map((item, index) => {
      const secItem = (index + 1).toString().padStart(3, '0');

      return {
        dSecItem: secItem,
        d_sec_item: secItem,
        dDescProd: item.description ? `${item.productName} - ${item.description}` : item.productName,
        d_desc_prod: item.description ? `${item.productName} - ${item.description}` : item.productName,
        dCodProd: item.productCode,
        d_cod_prod: item.productCode,
        cUnidad: item.unit || 'und',
        c_unidad: item.unit || 'und',
        dCantCodInt: item.quantity.toString(),
        d_cant_cod_int: item.quantity.toString(),
        gPrecios: {
          dPrUnit: formatPriceAmount(item.unitPrice),
          d_pr_unit: formatPriceAmount(item.unitPrice),
          dPrUnitDesc: formatPriceAmount(item.unitDiscount),
          d_pr_unit_desc: formatPriceAmount(item.unitDiscount),
          dPrItem: formatPriceAmount(item.subtotal),
          d_pr_item: formatPriceAmount(item.subtotal),
          dValTotItem: formatPriceAmount(item.total),
          d_val_tot_item: formatPriceAmount(item.total),
        },
        g_precios: {
          dPrUnit: formatPriceAmount(item.unitPrice),
          d_pr_unit: formatPriceAmount(item.unitPrice),
          dPrUnitDesc: formatPriceAmount(item.unitDiscount),
          d_pr_unit_desc: formatPriceAmount(item.unitDiscount),
          dPrItem: formatPriceAmount(item.subtotal),
          d_pr_item: formatPriceAmount(item.subtotal),
          dValTotItem: formatPriceAmount(item.total),
          d_val_tot_item: formatPriceAmount(item.total),
        },
        gItbmsItem: {
          dTasaItbms: mapITBMSRate(item.taxRate),
          d_tasa_itbms: mapITBMSRate(item.taxRate),
          dValItbms: formatPriceAmount(item.taxAmount),
          d_val_itbms: formatPriceAmount(item.taxAmount),
        },
        g_itbms_item: {
          dTasaItbms: mapITBMSRate(item.taxRate),
          d_tasa_itbms: mapITBMSRate(item.taxRate),
          dValItbms: formatPriceAmount(item.taxAmount),
          d_val_itbms: formatPriceAmount(item.taxAmount),
        },
      };
    });

    // Map payment methods
    const gFormaPago = data.payments && data.payments.length > 0
      ? data.payments.map(payment => ({
          iFormaPago: payment.method,
          i_forma_pago: payment.method,
          dVlrCuota: formatAmount(payment.amount),
          d_vlr_cuota: formatAmount(payment.amount),
          paymentMethodText: paymentMethodLabels[payment.method] || 'Otro',
        }))
      : [{
          iFormaPago: data.paymentMethod || '02',
          i_forma_pago: data.paymentMethod || '02',
          dVlrCuota: formatAmount(data.amountPaid || data.grandTotal),
          d_vlr_cuota: formatAmount(data.amountPaid || data.grandTotal),
          paymentMethodText: paymentMethodLabels[data.paymentMethod || '02'] || 'Efectivo',
        }];

    // Calcular vuelto
    const calculatedChange = (() => {
      if (data.paymentCondition === '2' || data.paymentCondition === '3') return '0.00';
      const totalPaid = data.payments && data.payments.length > 0
        ? data.payments.reduce((sum, p) => sum + p.amount, 0)
        : data.amountPaid;
      const change = totalPaid - data.grandTotal;
      return formatAmount(change > 0 ? change : 0);
    })();

    // Build FElectronica structure
    const FElectronica = {
      dGen: {
        iDoc: data.documentType,
        i_doc: data.documentType,
        dFechaEm: `${data.invoiceDate}T${getCurrentTime()}`,
        d_fecha_em: `${data.invoiceDate}T${getCurrentTime()}`,
        dNroDF: 'XXXXXXXXXX', // Preview placeholder
        d_nro_df: 'XXXXXXXXXX',
        gEmis: {
          dNombEm: data.issuerName,
          d_nomb_em: data.issuerName,
          dSucEm: data.branchOfficeAddressCode || '0001',
          d_suc_em: data.branchOfficeAddressCode || '0001',
          dDirecEm: data.branchOfficeAddress,
          d_direc_em: data.branchOfficeAddress,
          gRucEmi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          g_ruc_emi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          gUbiEm: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          g_ubi_em: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          dTfnEm: data.issuerPhone || '',
          d_tfn_em: data.issuerPhone || '',
          dCorElectEm: data.issuerEmail || '',
          d_cor_elect_em: data.issuerEmail || '',
        },
        g_emis: {
          dNombEm: data.issuerName,
          d_nomb_em: data.issuerName,
          dSucEm: data.branchOfficeAddressCode || '0001',
          d_suc_em: data.branchOfficeAddressCode || '0001',
          dDirecEm: data.branchOfficeAddress,
          d_direc_em: data.branchOfficeAddress,
          gRucEmi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          g_ruc_emi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          gUbiEm: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          g_ubi_em: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          dTfnEm: data.issuerPhone || '',
          d_tfn_em: data.issuerPhone || '',
          dCorElectEm: data.issuerEmail || '',
          d_cor_elect_em: data.issuerEmail || '',
        },
        gDatRec: {
          dNombRec: data.customerName,
          d_nomb_rec: data.customerName,
          iTipoRec: receiverTypeLabels[data.receiverType] || data.receiverType,
          i_tipo_rec: receiverTypeLabels[data.receiverType] || data.receiverType,
          dDirecRec: data.customerAddress,
          d_direc_rec: data.customerAddress,
          gRucRec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          g_ruc_rec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          gUbiRec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          g_ubi_rec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          dTfnRec: data.customerPhone || '',
          d_tfn_rec: data.customerPhone || '',
          dCorElectRec: data.customerEmail || '',
          d_cor_elect_rec: data.customerEmail || '',
        },
        g_dat_rec: {
          dNombRec: data.customerName,
          d_nomb_rec: data.customerName,
          iTipoRec: receiverTypeLabels[data.receiverType] || data.receiverType,
          i_tipo_rec: receiverTypeLabels[data.receiverType] || data.receiverType,
          dDirecRec: data.customerAddress,
          d_direc_rec: data.customerAddress,
          gRucRec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          g_ruc_rec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          gUbiRec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          g_ubi_rec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          dTfnRec: data.customerPhone || '',
          d_tfn_rec: data.customerPhone || '',
          dCorElectRec: data.customerEmail || '',
          d_cor_elect_rec: data.customerEmail || '',
        },
      },
      d_gen: {
        iDoc: data.documentType,
        i_doc: data.documentType,
        dFechaEm: `${data.invoiceDate}T${getCurrentTime()}`,
        d_fecha_em: `${data.invoiceDate}T${getCurrentTime()}`,
        dNroDF: 'XXXXXXXXXX',
        d_nro_df: 'XXXXXXXXXX',
        gEmis: {
          dNombEm: data.issuerName,
          d_nomb_em: data.issuerName,
          dSucEm: data.branchOfficeAddressCode || '0001',
          d_suc_em: data.branchOfficeAddressCode || '0001',
          dDirecEm: data.branchOfficeAddress,
          d_direc_em: data.branchOfficeAddress,
          gRucEmi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          g_ruc_emi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          gUbiEm: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          g_ubi_em: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          dTfnEm: data.issuerPhone || '',
          d_tfn_em: data.issuerPhone || '',
          dCorElectEm: data.issuerEmail || '',
          d_cor_elect_em: data.issuerEmail || '',
        },
        g_emis: {
          dNombEm: data.issuerName,
          d_nomb_em: data.issuerName,
          dSucEm: data.branchOfficeAddressCode || '0001',
          d_suc_em: data.branchOfficeAddressCode || '0001',
          dDirecEm: data.branchOfficeAddress,
          d_direc_em: data.branchOfficeAddress,
          gRucEmi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          g_ruc_emi: {
            dRuc: data.issuerCode,
            d_ruc: data.issuerCode,
            dDv: data.issuerDv,
            d_dv: data.issuerDv,
          },
          gUbiEm: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          g_ubi_em: {
            dCorreg: data.branchOfficeTownship,
            d_correg: data.branchOfficeTownship,
            dDistr: data.branchOfficeDistrict,
            d_distr: data.branchOfficeDistrict,
            dProv: data.branchOfficeProvince,
            d_prov: data.branchOfficeProvince,
          },
          dTfnEm: data.issuerPhone || '',
          d_tfn_em: data.issuerPhone || '',
          dCorElectEm: data.issuerEmail || '',
          d_cor_elect_em: data.issuerEmail || '',
        },
        gDatRec: {
          dNombRec: data.customerName,
          d_nomb_rec: data.customerName,
          iTipoRec: receiverTypeLabels[data.receiverType] || data.receiverType,
          i_tipo_rec: receiverTypeLabels[data.receiverType] || data.receiverType,
          dDirecRec: data.customerAddress,
          d_direc_rec: data.customerAddress,
          gRucRec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          g_ruc_rec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          gUbiRec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          g_ubi_rec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          dTfnRec: data.customerPhone || '',
          d_tfn_rec: data.customerPhone || '',
          dCorElectRec: data.customerEmail || '',
          d_cor_elect_rec: data.customerEmail || '',
        },
        g_dat_rec: {
          dNombRec: data.customerName,
          d_nomb_rec: data.customerName,
          iTipoRec: receiverTypeLabels[data.receiverType] || data.receiverType,
          i_tipo_rec: receiverTypeLabels[data.receiverType] || data.receiverType,
          dDirecRec: data.customerAddress,
          d_direc_rec: data.customerAddress,
          gRucRec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          g_ruc_rec: data.receiverType !== '04' ? {
            dRuc: data.customerRuc || '',
            d_ruc: data.customerRuc || '',
            dDv: data.customerDv || '',
            d_dv: data.customerDv || '',
          } : null,
          gUbiRec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          g_ubi_rec: data.receiverType !== '04' ? {
            dCorreg: data.customerTownship || '',
            d_correg: data.customerTownship || '',
            dDistr: data.customerDistrict || '',
            d_distr: data.customerDistrict || '',
            dProv: data.customerProvince || '',
            d_prov: data.customerProvince || '',
          } : null,
          dTfnRec: data.customerPhone || '',
          d_tfn_rec: data.customerPhone || '',
          dCorElectRec: data.customerEmail || '',
          d_cor_elect_rec: data.customerEmail || '',
        },
      },
      gItem,
      g_item: gItem,
      gTot: {
        dTotNeto: formatAmount(data.subtotal),
        d_tot_neto: formatAmount(data.subtotal),
        dTotItbms: formatAmount(data.totalTax),
        d_tot_itbms: formatAmount(data.totalTax),
        dTotIsc: formatAmount(data.totalIsc),
        d_tot_isc: formatAmount(data.totalIsc),
        dTotGravado: formatAmount(data.totalTax + data.totalIsc),
        d_tot_gravado: formatAmount(data.totalTax + data.totalIsc),
        dTotDesc: formatAmount(data.totalDiscount),
        d_tot_desc: formatAmount(data.totalDiscount),
        dVTot: formatAmount(data.grandTotal),
        d_v_tot: formatAmount(data.grandTotal),
        dTotRec: formatAmount(data.grandTotal),
        d_tot_rec: formatAmount(data.grandTotal),
        dVuelto: calculatedChange,
        d_vuelto: calculatedChange,
        gFormaPago,
        g_forma_pago: gFormaPago,
      },
      g_tot: {
        dTotNeto: formatAmount(data.subtotal),
        d_tot_neto: formatAmount(data.subtotal),
        dTotItbms: formatAmount(data.totalTax),
        d_tot_itbms: formatAmount(data.totalTax),
        dTotIsc: formatAmount(data.totalIsc),
        d_tot_isc: formatAmount(data.totalIsc),
        dTotGravado: formatAmount(data.totalTax + data.totalIsc),
        d_tot_gravado: formatAmount(data.totalTax + data.totalIsc),
        dTotDesc: formatAmount(data.totalDiscount),
        d_tot_desc: formatAmount(data.totalDiscount),
        dVTot: formatAmount(data.grandTotal),
        d_v_tot: formatAmount(data.grandTotal),
        dTotRec: formatAmount(data.grandTotal),
        d_tot_rec: formatAmount(data.grandTotal),
        dVuelto: calculatedChange,
        d_vuelto: calculatedChange,
        gFormaPago,
        g_forma_pago: gFormaPago,
      },
    };

    return {
      FElectronica,
      // Additional fields expected by template
      DocumentTitle: documentTypeLabels[data.documentType] || 'Factura',
      CompanyName: data.issuerName,
      BranchOfficeName: data.branchOfficeName || 'Principal',
      Number: 'VISTA PREVIA',
      CUFE: 'XXXX-XXXX-XXXX-XXXX (Vista Previa)',
      Cufe: 'XXXX-XXXX-XXXX-XXXX (Vista Previa)',
      CreatedDate: currentDate,
      CreatedHour: currentTime,
      Email: data.customerEmail,
      Subtotal: formatAmount(data.subtotal),
      TotalTax: formatAmount(data.totalTax),
      Total: formatAmount(data.grandTotal),
      AuthProtocol: 'VISTA PREVIA - Sin protocolo de autorización',
      Qrcode: '', // No QR for preview
      SeguroAcarreoItem: '0.00',
      TotNetoWithSeguroAcarreo: formatAmount(data.subtotal),
      // Items for simple templates
      Items: data.items.map((item, index) => ({
        Sequence: (index + 1).toString(),
        Description: `${item.productName} - ${item.description}`,
        Code: item.productCode,
        Quantity: item.quantity.toString(),
        UnitPrice: formatPriceAmount(item.unitPrice),
        Discount: formatPriceAmount(item.unitDiscount),
        Tax: formatPriceAmount(item.taxAmount),
        Total: formatPriceAmount(item.total),
      })),
      // Impuestos breakdown
      Impuestos: [
        {
          Descripcion: 'ITBMS',
          descripcion: 'ITBMS',
          Monto: formatAmount(data.totalTax),
          monto: formatAmount(data.totalTax),
          MontoImpuesto: formatAmount(data.totalTax),
          monto_impuesto: formatAmount(data.totalTax),
        },
      ],
    };
  };

  // Helper function to sanitize HTML
  const sanitizeHtml = (html: string): string => {
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');
    return sanitized;
  };

  // Helper function to convert snake_case template to camelCase
  const snakeCaseToPascalCaseTemplate = (templateHtml: string): string => {
    return templateHtml.replace(/_([a-zA-Z])/g, (_match, letter) => {
      return letter.toUpperCase();
    });
  };

  // Convert Jinja2/DotLiquid to Mustache
  const convertJinja2ToMustache = (templateHtml: string, colors: { background: string; primary: string; text: string }): string => {
    let result = templateHtml;

    // Replace CSS variables
    const cssVarMap: Record<string, string> = {
      BackgroundHeaderColor: colors.primary,
      BackgroundBodyColor: colors.background,
      BackgroundBodyDecorationColor: colors.primary,
      BackgroundFooterColor: colors.background,
      TextPrimaryColor: colors.text,
      TextSegundaryColor: colors.text,
      ButtonRedirectWebSiteColor: colors.background,
      TextButtonRedirectWebSiteColor: colors.primary,
      ButtonRedirectEivoiceColor: colors.primary,
      TextButtonRedirectEivoiceColor: colors.background,
    };

    Object.keys(cssVarMap).forEach((varName) => {
      const regex = new RegExp(`var\\(--${varName}\\s*\\)`, 'g');
      result = result.replace(regex, cssVarMap[varName]);
    });

    // Convert for loops
    interface LoopPosition {
      type: 'for' | 'endfor';
      pos: number;
      endPos: number;
      arrayName?: string;
    }

    const positions: LoopPosition[] = [];
    const forRegex = /\{% for (\w+) in ([\w.]+) %\}/g;
    let match;

    while ((match = forRegex.exec(result)) !== null) {
      positions.push({
        type: 'for',
        pos: match.index,
        endPos: match.index + match[0].length,
        arrayName: match[2]
      });
    }

    const endforRegex = /\{% endfor %\}/g;
    while ((match = endforRegex.exec(result)) !== null) {
      positions.push({
        type: 'endfor',
        pos: match.index,
        endPos: match.index + match[0].length
      });
    }

    positions.sort((a, b) => a.pos - b.pos);

    interface LoopContext {
      arrayName: string;
      varName: string;
      startPos: number;
      endPos: number;
    }

    const stack: LoopContext[] = [];
    const replacements: Array<{ pos: number; endPos: number; replacement: string }> = [];
    const loopContexts: LoopContext[] = [];

    const forRegex2 = /\{% for (\w+) in ([\w.]+) %\}/g;
    const forPositions: Array<{ pos: number; endPos: number; varName: string; arrayName: string }> = [];

    while ((match = forRegex2.exec(result)) !== null) {
      forPositions.push({
        pos: match.index,
        endPos: match.index + match[0].length,
        varName: match[1],
        arrayName: match[2]
      });
    }

    positions.forEach((position) => {
      if (position.type === 'for' && position.arrayName) {
        const forPos = forPositions.find(fp => fp.pos === position.pos);
        if (forPos) {
          const context: LoopContext = {
            arrayName: position.arrayName,
            varName: forPos.varName,
            startPos: position.endPos,
            endPos: -1
          };
          stack.push(context);
          replacements.push({
            pos: position.pos,
            endPos: position.endPos,
            replacement: `{{#${position.arrayName}}}`
          });
        }
      } else if (position.type === 'endfor') {
        const context = stack.pop();
        if (context) {
          context.endPos = position.pos;
          loopContexts.push(context);
          replacements.push({
            pos: position.pos,
            endPos: position.endPos,
            replacement: `{{/${context.arrayName}}}`
          });
        }
      }
    });

    replacements.sort((a, b) => b.pos - a.pos);
    replacements.forEach((repl) => {
      const before = result.substring(0, repl.pos);
      const after = result.substring(repl.endPos);
      result = before + repl.replacement + after;
    });

    // Remove loop variable prefixes
    loopContexts.forEach(context => {
      const varPrefixRegex = new RegExp(`\\{\\{${context.varName}\\.([\\w.]+)\\}\\}`, 'g');
      result = result.replace(varPrefixRegex, (_match, propertyPath) => `{{${propertyPath}}}`);
    });

    // Replace payment method conditionals
    const formaPagoPattern = /\{%\s*if\s+Pago\.(i_forma_pago|iFormaPago)\s*==\s*"01"\s*%\}[\s\S]*?(?:\{%\s*elsif\s+Pago\.(i_forma_pago|iFormaPago)\s*==\s*"[^"]+"\s*%\}[\s\S]*?)*\{%\s*endif\s*%\}/g;
    result = result.replace(formaPagoPattern, '<td>{{paymentMethodText}}</td>');

    // Replace receiver type conditionals (iTipoRec)
    const tipoRecPattern = /\{%\s*if\s+(?:FElectronica\.g_dat_rec\.|gDatRec\.)?(?:i_tipo_rec|iTipoRec)\s*==\s*"[^"]+"\s*%\}[\s\S]*?(?:\{%\s*elsif\s+(?:FElectronica\.g_dat_rec\.|gDatRec\.)?(?:i_tipo_rec|iTipoRec)\s*==\s*"[^"]+"\s*%\}[\s\S]*?)*\{%\s*endif\s*%\}/g;
    result = result.replace(tipoRecPattern, '{{iTipoRec}}');

    // Remove remaining Jinja2 tags
    result = result.replace(/\{% else %\}[\s\S]*?\{% endif %\}/g, '{% endif %}');
    result = result.replace(/\{%\s*if\s+[^%]+\s*%\}/g, '');
    result = result.replace(/\{%\s*elsif\s+[^%]+\s*%\}/g, '');
    result = result.replace(/\{%\s*endif\s*%\}/g, '');
    result = result.replace(/\{%\s*else\s*%\}/g, '');
    result = result.replace(/\{\{#if\s+([^}]+)\}\}/g, '');
    result = result.replace(/\{\{\/if\}\}/g, '');

    return result;
  };

  // Load and render template when modal opens
  useEffect(() => {
    if (!isOpen || !cafeTemplate || !templateData?.template) return;

    const renderPreview = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const colors = {
          background: templateData.template?.emailTemplate?.colorbackground || '#FFFFFF',
          primary: templateData.template?.emailTemplate?.colorprimary || '#12C4B2',
          text: templateData.template?.emailTemplate?.colortext || '#333333',
        };

        const logo = templateData.template?.emailTemplate?.logo || '';

        // Transform wizard data to template format
        const templateDataObj = transformWizardDataToFElectronica(wizardData);

        // Add logo and colors
        const finalTemplateData = {
          ...templateDataObj,
          Logo: logo,
          logo: logo,
          ColorPrimary: colors.primary,
          ColorHeader: colors.primary,
          ColorFooter: colors.background,
        };

        // Transform template HTML from snake_case to camelCase
        const transformedTemplateHtml = snakeCaseToPascalCaseTemplate(cafeTemplate.templateHtml);

        // Convert Jinja2 to Mustache
        let mustacheTemplate = convertJinja2ToMustache(transformedTemplateHtml, colors);

        // Replace arithmetic expressions
        mustacheTemplate = mustacheTemplate.replace(
          /\{\{FElectronica\.g_tot\.d_tot_neto \+ SeguroAcarreoItem\}\}/g,
          '{{TotNetoWithSeguroAcarreo}}'
        );

        // Render with Mustache
        const html = Mustache.render(mustacheTemplate, finalTemplateData);

        // Create wrapped HTML
        const wrappedHtml = `
<!DOCTYPE html>
<html>
<head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta charset="utf-8" />
    <style>
        body {
            overflow: auto;
            margin: 0;
            padding: 0;
        }
        body::-webkit-scrollbar {
            width: 8px;
            height: 8px;
        }
        body::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 4px;
        }
        body::-webkit-scrollbar-track {
            background: #e1e1e1;
            border-radius: 4px;
        }
        #frame-cafeTemplates-contend {
            background-color: white;
            padding: 20px;
        }
        /* Justificar texto de descripcion de productos*/
        #frame-cafeTemplates-contend td {
            text-align: justify;
            }
        /* Preview watermark */
        .preview-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 80px;
            font-weight: bold;
            color: rgba(0, 0, 0, 0.05);
            pointer-events: none;
            z-index: 9999;
            white-space: nowrap;
        }
    </style>
</head>
<body>
    <div class="preview-watermark">VISTA PREVIA</div>
    <div id="frame-cafeTemplates-contend">
        ${html}
    </div>
</body>
</html>
        `.trim();

        const sanitizedHtml = sanitizeHtml(wrappedHtml);
        setRenderedHtml(sanitizedHtml);
      } catch (err) {
        console.error('Error rendering preview:', err);
        setError(err instanceof Error ? err.message : 'Error al generar la vista previa');
      } finally {
        setIsLoading(false);
      }
    };

    renderPreview();
  }, [isOpen, cafeTemplate, templateData, wizardData]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Vista Previa de Factura"
      size="full"
    >
      <div className="space-y-4">
        {/* Info banner */}
        <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-3">
          <p className="text-sm text-info-800 dark:text-info-200">
            Esta es una vista previa de cómo se verá la factura una vez emitida.
            Algunos campos como el número de factura y el CUFE se generarán al momento de la emisión.
          </p>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner />
            <p className="mt-4 text-secondary-600 dark:text-secondary-400">
              Generando vista previa...
            </p>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        {!isLoading && !error && renderedHtml && (
          <div
            className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-auto"
            style={{
              maxHeight: '75vh',
              maxWidth: '100%'
            }}
          >
            <iframe
              title="Invoice Preview"
              className="bg-white"
              style={{
                border: 'none',
                paddingTop: '10px',
                width: '100%',
                height: '1000px'
              }}
              srcDoc={renderedHtml}
              sandbox="allow-same-origin"
            />
          </div>
        )}

        {!isLoading && !cafeTemplate && (
          <Alert variant="warning">
            No se encontró un template CAFE configurado. Por favor, configure un template en la sección de Templates.
          </Alert>
        )}
      </div>
    </Modal>
  );
}
